// Offline detection utility
// Detects when device goes offline and provides callbacks for handling offline/online states

let offlineListeners = [];
let onlineListeners = [];
let isCurrentlyOffline = false;

/**
 * Checks if device is currently offline
 * @returns {boolean} True if offline
 */
export function isOffline() {
    return !navigator.onLine || isCurrentlyOffline;
}

/**
 * Adds listener for offline events
 * @param {Function} callback - Function to call when device goes offline
 * @returns {Function} Unsubscribe function
 */
export function onOffline(callback) {
    if (typeof callback !== 'function') {
        console.warn('onOffline callback must be a function');
        return () => {};
    }
    
    offlineListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
        const index = offlineListeners.indexOf(callback);
        if (index > -1) {
            offlineListeners.splice(index, 1);
        }
    };
}

/**
 * Adds listener for online events
 * @param {Function} callback - Function to call when device comes back online
 * @returns {Function} Unsubscribe function
 */
export function onOnline(callback) {
    if (typeof callback !== 'function') {
        console.warn('onOnline callback must be a function');
        return () => {};
    }
    
    onlineListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
        const index = onlineListeners.indexOf(callback);
        if (index > -1) {
            onlineListeners.splice(index, 1);
        }
    };
}

/**
 * Notifies all offline listeners
 */
function notifyOfflineListeners() {
    isCurrentlyOffline = true;
    offlineListeners.forEach(callback => {
        try {
            callback();
        } catch (error) {
            console.error('Error in offline listener:', error);
        }
    });
}

/**
 * Notifies all online listeners
 */
function notifyOnlineListeners() {
    isCurrentlyOffline = false;
    onlineListeners.forEach(callback => {
        try {
            callback();
        } catch (error) {
            console.error('Error in online listener:', error);
        }
    });
}

/**
 * Handles offline event
 */
function handleOffline() {
    console.warn('Device went offline');
    notifyOfflineListeners();
}

/**
 * Handles online event
 */
function handleOnline() {
    console.info('Device came back online');
    // Verify connection by attempting a lightweight request
    verifyConnection().then(isConnected => {
        if (isConnected) {
            notifyOnlineListeners();
        } else {
            console.warn('Device reports online but connection verification failed');
        }
    });
}

/**
 * Verifies connection by attempting to fetch a lightweight resource
 * @returns {Promise<boolean>} True if connection is verified
 */
async function verifyConnection() {
    try {
        // Try to fetch a small resource with a short timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        
        const response = await fetch('/favicon.ico', {
            method: 'HEAD',
            signal: controller.signal,
            cache: 'no-cache'
        });
        
        clearTimeout(timeoutId);
        return response.ok;
    } catch (error) {
        console.warn('Connection verification failed:', error);
        return false;
    }
}

/**
 * Initializes offline/online event listeners
 */
export function initializeOfflineDetection() {
    // Set initial state
    isCurrentlyOffline = !navigator.onLine;
    
    // Add event listeners
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    
    // Also listen to visibility change as some browsers don't fire online/offline events reliably
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            // Check connection when tab becomes visible
            verifyConnection().then(isConnected => {
                if (isConnected && isCurrentlyOffline) {
                    handleOnline();
                } else if (!isConnected && !isCurrentlyOffline) {
                    handleOffline();
                }
            });
        }
    });
    
    console.debug('Offline detection initialized. Initial state:', isCurrentlyOffline ? 'offline' : 'online');
}

/**
 * Cleans up event listeners
 */
export function cleanupOfflineDetection() {
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('online', handleOnline);
    offlineListeners = [];
    onlineListeners = [];
}

// Auto-initialize when module loads
if (typeof window !== 'undefined') {
    initializeOfflineDetection();
}

export const offlineDetector = {
    isOffline,
    onOffline,
    onOnline,
    initializeOfflineDetection,
    cleanupOfflineDetection
};

